import { FC } from 'react';

type LockedScoreCardProps = {
  /** The user's readiness score (0-100), drawn as the amber fill. */
  fillPercent: number;
  /** The winners target (0-100), drawn as the green marker. */
  targetPercent: number;
  label: string;
  lockedLabel: string;
  /** The obscured score shown in the pill at the fill tip, e.g. "??". */
  obscuredScoreLabel: string;
};

const PadlockIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/**
 * The inline locked score card on the email gate (spec 6.1 step 7): the
 * bar is filled to the user's true position with the winners marker
 * visible, but the score itself stays obscured until after the reveal.
 */
const LockedScoreCard: FC<LockedScoreCardProps> = ({
  fillPercent,
  targetPercent,
  label,
  lockedLabel,
  obscuredScoreLabel
}) => {
  const clampedFill = Math.max(0, Math.min(100, fillPercent));
  const clampedTarget = Math.max(0, Math.min(100, targetPercent));

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white px-5 pb-6 pt-5 shadow-100">
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="font-body text-xs font-bold uppercase tracking-widest text-primary-500">
          {label}
        </p>
        <p className="flex items-center gap-1.5 font-heading text-sm font-bold text-neutral-600">
          <span className="text-primary-500">
            <PadlockIcon />
          </span>
          {lockedLabel}
        </p>
      </div>
      <div className="relative">
        <div className="h-2.5 overflow-hidden rounded-full bg-neutral-200">
          <div
            className="h-full rounded-full bg-primary-500"
            style={{ width: `${clampedFill}%` }}
          />
        </div>
        {/* Obscured score pill at the fill tip */}
        <span
          className="absolute -top-2 z-10 -translate-x-1/2 rounded-full border-2 border-primary-500 bg-white px-2.5 py-0.5 font-heading text-xs font-bold tracking-widest text-primary-600"
          style={{ left: `${clampedFill}%` }}
        >
          {obscuredScoreLabel}
        </span>
        {/* Winners target marker */}
        <span
          className="absolute -top-1 h-[18px] w-0.5 -translate-x-1/2 rounded bg-greenfriday"
          style={{ left: `${clampedTarget}%` }}
        />
      </div>
    </div>
  );
};

export default LockedScoreCard;
